/**
 * Escalation Models
 *
 * NOTE: These are app-specific types for escalation management.
 * There are no corresponding library types in @open/nexus-public-api.
 *
 * This module provides:
 * - Escalation events and notifications
 * - Escalation policies with chain/depth limits
 * - Ticket state conditions for escalation triggers
 * - Validation and blocking rules
 */

import type { EscalationRole, EscalationAction } from '../../sla'

/**
 * Ticket states for escalation conditions
 */
export type TicketState =
  | 'new'
  | 'open'
  | 'in_progress'
  | 'pending'
  | 'on_hold'
  | 'resolved'
  | 'closed'

// Событие эскалации
export interface EscalationEvent {
  id: string
  ticketId: string
  ticketNumber: string
  slaConfigId: string
  ruleId: string

  // Детали эскалации
  triggeredAt: number
  hoursElapsed: number
  notifyRole: EscalationRole
  notifiedUserIds: string[]
  action: EscalationAction

  // Результаты
  notificationsSent: EscalationNotification[]
  wasReassigned: boolean
  reassignedToUserId?: string
  reassignedToQueueId?: string
  priorityChanged?: boolean
  newPriority?: 'high' | 'mid' | 'low'

  // Escalation chain tracking
  escalationDepth: number           // Current depth in escalation chain
  parentEscalationId?: string       // ID of parent escalation if chained
  ticketStateAtTrigger?: TicketState // Ticket state when escalation was triggered

  // Метаданные
  createdAt: number
  processedAt?: number
  status: EscalationStatus
  errorMessage?: string
}

// Статусы эскалации
export enum EscalationStatus {
  PENDING = 'pending',           // Ожидает обработки
  PROCESSING = 'processing',     // Обрабатывается
  COMPLETED = 'completed',       // Завершена
  FAILED = 'failed'              // Ошибка
}

// Уведомление об эскалации
export interface EscalationNotification {
  userId: string
  userName: string
  method: 'email' | 'sms' | 'push' | 'system'
  sentAt: number
  delivered: boolean
  errorMessage?: string
}

// Политика эскалации
export interface EscalationPolicy {
  id: string
  name: string
  nameRu: string
  description?: string

  // Применимость
  slaConfigIds: string[]         // К каким SLA применяется
  categoryIds?: string[]         // К каким категориям применяется

  // Настройки уведомлений
  notificationMethods: ('email' | 'sms' | 'push' | 'system')[]
  notificationTemplateId?: string

  // Настройки переназначения
  autoReassignEnabled: boolean
  reassignmentDelay?: number     // Задержка в минутах перед переназначением

  // Повышение приоритета
  autoPriorityIncrease: boolean

  // Escalation chain limits
  maxEscalationDepth: number           // Maximum escalation depth (default: 5)
  coolingPeriodMinutes?: number        // Minimum time between escalations (default: 30)

  // State conditions
  allowedStates: TicketState[]         // Only escalate when ticket is in these states
  blockedStates: TicketState[]         // Never escalate when ticket is in these states
  pauseOnStates?: TicketState[]        // Pause SLA timer when in these states (e.g., 'on_hold')

  isActive: boolean
  createdAt: number
  updatedAt: number
}

// Запрос на эскалацию
export interface EscalationRequest {
  ticketId: string
  ticketNumber: string
  slaConfigId: string
  ruleId: string
  hoursElapsed: number
  currentPriority: 'high' | 'mid' | 'low'
  currentAssigneeId?: string
  currentQueueId?: string
  // State tracking
  ticketState: TicketState
  currentEscalationDepth: number
  lastEscalationAt?: number
  parentEscalationId?: string
}

// История эскалаций тикета
export interface TicketEscalationHistory {
  ticketId: string
  ticketNumber: string
  events: EscalationEvent[]
  totalEscalations: number
  lastEscalatedAt?: number
}

// Метрики эскалаций
export interface EscalationMetrics {
  period: {
    start: number
    end: number
  }
  totalEscalations: number
  byRole: Record<EscalationRole, number>
  byAction: Record<EscalationAction, number>
  byStatus: Record<EscalationStatus, number>
  averageResponseTime: number    // В минутах
  successRate: number            // Процент успешных эскалаций
}

// Локализация статусов
export const ESCALATION_STATUS_LABELS: Record<EscalationStatus, string> = {
  [EscalationStatus.PENDING]: 'Ожидает',
  [EscalationStatus.PROCESSING]: 'Обрабатывается',
  [EscalationStatus.COMPLETED]: 'Завершена',
  [EscalationStatus.FAILED]: 'Ошибка'
}

// Цвета статусов
export const ESCALATION_STATUS_COLORS: Record<EscalationStatus, string> = {
  [EscalationStatus.PENDING]: 'text-blue-600 dark:text-blue-400',
  [EscalationStatus.PROCESSING]: 'text-yellow-600 dark:text-yellow-400',
  [EscalationStatus.COMPLETED]: 'text-green-600 dark:text-green-400',
  [EscalationStatus.FAILED]: 'text-red-600 dark:text-red-400'
}

/**
 * Escalation blocking reasons
 */
export type EscalationBlockReason =
  | 'max_depth_exceeded'      // Maximum escalation depth reached
  | 'cooling_period_active'   // Waiting for cooling period to expire
  | 'state_not_allowed'       // Ticket state not in allowed list
  | 'state_blocked'           // Ticket state is in blocked list
  | 'sla_paused'              // SLA timer is paused (e.g., on_hold)
  | 'already_triggered'       // Rule already triggered for this ticket
  | 'time_not_reached'        // Time threshold not yet reached
  | 'policy_inactive'         // Policy is inactive

/**
 * Result of escalation validation
 */
export interface EscalationValidationResult {
  canEscalate: boolean
  blockReason?: EscalationBlockReason
  details?: string
  suggestedWaitTime?: number    // Minutes until escalation can proceed
  currentDepth?: number
  maxDepth?: number
}

/**
 * Escalation chain info
 */
export interface EscalationChainInfo {
  ticketId: string
  currentDepth: number
  maxDepth: number
  isAtMaxDepth: boolean
  chain: Array<{
    escalationId: string
    level: number
    triggeredAt: number
    notifyRole: EscalationRole
  }>
  canEscalate: boolean
  nextAvailableAt?: number    // Timestamp when cooling period expires
}

/**
 * Default escalation policy values
 */
export const DEFAULT_ESCALATION_POLICY_VALUES = {
  maxEscalationDepth: 5,
  coolingPeriodMinutes: 30,
  allowedStates: ['new', 'open', 'in_progress', 'pending'] as TicketState[],
  blockedStates: ['resolved', 'closed'] as TicketState[],
  pauseOnStates: ['on_hold'] as TicketState[]
}
